import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { cn } from '../../lib/cn'

interface DropdownItem {
  label: string
  onSelect: () => void
  icon?: React.ReactNode
  tone?: 'default' | 'danger'
}

interface DropdownMenuProps {
  trigger: (props: {
    ref: React.Ref<HTMLButtonElement>
    toggleProps: React.ButtonHTMLAttributes<HTMLButtonElement>
  }) => React.ReactNode
  items: ReadonlyArray<DropdownItem>
  align?: 'left' | 'right'
}

/** Menu-button pattern (role=menu/menuitem, arrows cycle, Esc restores focus). */
export function DropdownMenu({ trigger, items, align = 'right' }: DropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
  const triggerId = useId()

  const close = useCallback((refocus = false) => {
    setOpen(false)
    if (refocus) triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true)
    }
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClick)
    itemRefs.current[0]?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open, close])

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    const idx = itemRefs.current.findIndex((el) => el === document.activeElement)
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      itemRefs.current[(idx + 1) % items.length]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      itemRefs.current[(idx - 1 + items.length) % items.length]?.focus()
    } else if (e.key === 'Home') {
      e.preventDefault()
      itemRefs.current[0]?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      itemRefs.current[items.length - 1]?.focus()
    } else if (e.key === 'Tab') {
      // APG menu contract: Tab exits the widget. Close and hand focus back to
      // the trigger so the default Tab continues the page sequence from there.
      close(true)
    }
  }

  return (
    <div ref={rootRef} className="relative inline-flex">
      {trigger({
        ref: triggerRef,
        toggleProps: {
          id: triggerId,
          'aria-haspopup': 'menu',
          'aria-expanded': open,
          onClick: () => setOpen((o) => !o),
          onKeyDown: (e) => {
            if (e.key === 'ArrowDown' && !open) {
              e.preventDefault()
              setOpen(true)
            }
          },
        },
      })}
      {open && (
        <div
          role="menu"
          // The trigger names the menu (APG menu-button): "Account menu" etc.
          aria-labelledby={triggerId}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute top-full z-40 mt-2 min-w-44 overflow-hidden rounded-control',
            'bg-surface-raised py-1 ring-1 ring-border-warm shadow-warm',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {items.map((item, i) => (
            <button
              key={item.label}
              ref={(el) => {
                itemRefs.current[i] = el
              }}
              role="menuitem"
              type="button"
              // APG roving focus: items are focused programmatically (arrows/
              // Home/End), never part of the page tab sequence.
              tabIndex={-1}
              onClick={() => {
                close(true) // restore focus to the trigger, not document.body
                item.onSelect()
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm transition',
                'hover:bg-surface focus-visible:outline-none focus-visible:bg-surface',
                item.tone === 'danger' ? 'text-danger-fg' : 'text-text',
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
